import type { ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { ActivityApiRow, AiLogApiRow, ActivityScope, AiLogScope } from '@command-center/logging-sdk';
import { Badge, Tabs, TabsContent, TabsList, TabsTrigger } from '@command-center/ui-system';
import { ActivityLogViewer } from './ActivityLogViewer';
import { AiLogViewer } from './AiLogViewer';
import { LoggingHealthPanel, type LoggingHealthClient } from './LoggingHealthPanel';
import type { ActivityFilterOptions, ActivityLogClient, AiLogClient } from './types';

export type ActivityTabId = 'user' | 'system' | 'all' | 'ai' | 'health';

export interface PortalActivityPageProps {
  /** Which tabs this portal offers, in order. */
  tabs: ActivityTabId[];
  defaultTab?: ActivityTabId;
  activity: ActivityLogClient;
  /** Required whenever `tabs` includes 'ai'. */
  ai?: AiLogClient;
  /** Required whenever `tabs` includes 'health'. */
  health?: LoggingHealthClient;
  /**
   * The portal's fixed scope, applied to every tab on top of the tab's own
   * stream. The server applies it again; this keeps the browser from asking
   * for rows it will not be given.
   */
  scope?: ActivityScope;
  aiScope?: AiLogScope;
  filterOptions?: ActivityFilterOptions;
  hiddenFilters?: string[];
  /** Namespaces the URL filter parameters. Distinct per portal, not per tab. */
  storagePrefix?: string;
  labels?: Partial<Record<ActivityTabId, string>>;
  emptyMessages?: Partial<Record<ActivityTabId, string>>;
  searchPlaceholder?: string;
  renderActivityRowActions?: (row: ActivityApiRow) => ReactNode;
  renderAiRowActions?: (row: AiLogApiRow) => ReactNode;
}

const DEFAULT_LABELS: Record<ActivityTabId, string> = {
  user: 'User Activity',
  system: 'System Activity',
  all: 'All',
  ai: 'AI Logs',
  health: 'Health',
};

/**
 * The Activity page, for any portal.
 *
 * User Activity and System Activity are two filtered reads of the same
 * `activity_logs` table through the same API, not two logs. The split exists
 * because scheduled work, webhooks, and provider callbacks produce far more
 * rows than people do, and a combined default buries what anyone actually did.
 * User Activity is therefore the default, and where the portal can report
 * unresolved background work the System tab carries a count of it so nothing
 * important hides behind that choice.
 *
 * The `all` tab is retained deliberately: correlating an operator action with
 * the scheduled work it triggered needs both halves in one order.
 */
export function PortalActivityPage({
  tabs,
  defaultTab,
  activity,
  ai,
  health,
  scope,
  aiScope,
  filterOptions,
  hiddenFilters = [],
  storagePrefix = 'act',
  labels,
  emptyMessages,
  searchPlaceholder,
  renderActivityRowActions,
  renderAiRowActions,
}: PortalActivityPageProps) {
  const first = defaultTab && tabs.includes(defaultTab) ? defaultTab : tabs[0];
  const label = (id: ActivityTabId) => labels?.[id] ?? DEFAULT_LABELS[id];

  return (
    <Tabs defaultValue={first} className="space-y-3">
      <TabsList>
        {tabs.map((id) => (
          <TabsTrigger key={id} value={id}>
            {label(id)}
            {id === 'system' && activity.attention && <AttentionBadge client={activity} />}
          </TabsTrigger>
        ))}
      </TabsList>

      {tabs.includes('user') && (
        <TabsContent value="user">
          <ActivityLogViewer
            client={activity}
            storageKey={`${storagePrefix}-user`}
            scope={{ ...scope, stream: 'user' }}
            filterOptions={filterOptions}
            // Every row here has a person behind it, so an actor-type selector
            // would only ever narrow to a subset of the same thing.
            hiddenFilters={[...hiddenFilters, 'actorType']}
            labels={{
              empty: emptyMessages?.user ?? 'No user activity in this range.',
              searchPlaceholder,
            }}
            renderRowActions={renderActivityRowActions}
          />
        </TabsContent>
      )}

      {tabs.includes('system') && (
        <TabsContent value="system">
          <ActivityLogViewer
            client={activity}
            storageKey={`${storagePrefix}-system`}
            scope={{ ...scope, stream: 'system' }}
            filterOptions={filterOptions}
            hiddenFilters={hiddenFilters}
            showAttention
            labels={{
              empty: emptyMessages?.system ?? 'No system activity in this range.',
              searchPlaceholder,
            }}
            renderRowActions={renderActivityRowActions}
          />
        </TabsContent>
      )}

      {tabs.includes('all') && (
        <TabsContent value="all">
          <ActivityLogViewer
            client={activity}
            storageKey={storagePrefix}
            scope={scope}
            filterOptions={filterOptions}
            hiddenFilters={hiddenFilters}
            labels={{
              empty: emptyMessages?.all ?? 'No activity logged yet.',
              searchPlaceholder,
            }}
            renderRowActions={renderActivityRowActions}
          />
        </TabsContent>
      )}

      {tabs.includes('ai') && ai && (
        <TabsContent value="ai">
          <AiLogViewer
            client={ai}
            storageKey={`${storagePrefix}-ai`}
            scope={aiScope}
            labels={{ empty: emptyMessages?.ai }}
            renderRowActions={renderAiRowActions}
          />
        </TabsContent>
      )}

      {tabs.includes('health') && health && (
        <TabsContent value="health">
          <LoggingHealthPanel client={health} />
        </TabsContent>
      )}
    </Tabs>
  );
}

/**
 * Unresolved background failures, on the System tab itself.
 *
 * Only rendered when the portal actually has a queue to report on. A badge
 * counting another portal's jobs would be worse than no badge.
 */
function AttentionBadge({ client }: { client: ActivityLogClient }) {
  const { data } = useQuery({
    queryKey: ['activity', 'attention'],
    queryFn: () => client.attention!(),
    refetchInterval: 120_000,
  });
  const unresolved = data?.count ?? 0;
  if (!unresolved) return null;
  return (
    <Badge variant="destructive" className="ml-1.5">
      {unresolved}
    </Badge>
  );
}
